import React, { useEffect, useMemo, useState } from 'react';
import { LinkModel } from '../Types/LinkModel';
import CustomResponsiveSlider from '../Components/CustomResponsiveSlider/CustomResponsiveSlider';
import PageCardItemModel from '../Types/PageCardItemModel';
import PageCardItem from './PageCardItem';
import PageCardItemList from '../Components/NewsList/PageCardItemList';
import ListBlockHeader from './ListBlockHeader';
import clsx from 'clsx';
import { getBlockData } from '../Services/Block.service';

export interface PageCardBlockProps {
  items: PageCardItemModel[];
  numPerRow: number;
  showSlider: boolean;
  showInfo: boolean;
  preTitle?: string;
  title?: string;
  link?: LinkModel;
  mobileLayout?: 'small' | 'large';
}

const validAspectRatios = [
  'aspect-square',
  'aspect-[3/4]',
  'aspect-[4/3]',
  'aspect-[2/3]',
  'aspect-[3/2]',
];

const getFixedAspectRatios = (index: number) => {
  switch (index) {
    case 0:
    default:
      return 'aspect-[3/4]';
    case 1:
      return 'aspect-square';
    case 2:
      return 'lg:aspect-[2/3] md:aspect-square aspect-[4/3]';
    case 3:
      return 'xl:aspect-[4/3] lg:aspect-square aspect-[2/3]';
    case 4:
      return 'xl:aspect-square aspect-[2/3]';
    case 5:
      return 'xl:aspect-[2/3] lg:aspect-[3/4] aspect-[4/3]';
    case 6:
      return 'xl:aspect-[3/4] lg:aspect-[2/3] aspect-[4/3]';
    case 7:
      return 'xl:aspect-square lg:aspect-[4/3] aspect-square';
  }
};

const getRandomInt = (min: number, max: number) => {
  min = Math.ceil(min);
  max = Math.floor(max);
  return Math.floor(Math.random() * (max - min) + min); // The maximum is exclusive and the minimum is inclusive
};

const generateAspectRatio = (
  invalidAspectRatio: string | undefined = undefined
) => {
  let aspectRatio =
    validAspectRatios[getRandomInt(0, validAspectRatios.length - 1)] ??
    'aspect-square';

  // Generate new aspect ratio until we get a valid aspect ratio
  if (invalidAspectRatio) {
    while (aspectRatio == invalidAspectRatio) {
      let newAspectRatio =
        validAspectRatios[getRandomInt(0, validAspectRatios.length - 1)];
      if (!newAspectRatio) {
        return 'aspect-square';
      }
      aspectRatio = newAspectRatio;
    }
  }

  return aspectRatio;
};

const getAspectRatio = (index) => {
  switch (index % 4) {
    case 0:
    default:
      return 'aspect-[3/4]';
    case 1:
      return 'aspect-square';
    case 2:
      return 'aspect-[2/3]';
    case 3:
      return 'aspect-[4/3]';
  }
};

const withInitialProps = (items: PageCardItemModel[]) => {
  var _items = [...items];
  items.forEach((n, idx) => {
    if (!n.aspectRatio) {
      n.aspectRatio =
        idx < 8 ? getFixedAspectRatios(idx) : generateAspectRatio();
    }
  });
  return _items;
};

const PageCardBlock = ({ blockSystemId }: { blockSystemId: string }) => {
  const [blockProps, setBlockProps] = useState<
    PageCardBlockProps | undefined
  >();

  const {
    preTitle,
    title,
    link,
    items,
    numPerRow,
    showSlider,
    mobileLayout = 'small',
  } = blockProps ?? ({} as PageCardBlockProps);

  const pageCardItemListItems = useMemo(
    () => (items ? withInitialProps(items) : items),
    [items]
  );

  useEffect(() => {
    const getPageCardBlock = async () => {
      setBlockProps(await getBlockData('PageCardBlock', blockSystemId));
    };

    getPageCardBlock();
  }, [blockSystemId]);

  return blockProps ? (
    <>
      <div className={clsx(!showSlider && 'hidden')}>
        <CustomResponsiveSlider
          link={link}
          items={items}
          preTitle={preTitle}
          title={title}
          numPerRow={numPerRow}
          showSlider={showSlider}
          mobileLayout={mobileLayout}
          itemRenderer={(item, key, className, index) => (
            <PageCardItem
              key={key}
              className={className}
              item={item}
              aspectRatio={getAspectRatio(index) + ' sm-down:aspect-[3/4]'}
            />
          )}
        />
      </div>
      {!showSlider && (
        <>
          <ListBlockHeader
            showSlider={false}
            title={title}
            preTitle={preTitle}
            link={link}
          />
          <PageCardItemList
            items={pageCardItemListItems}
            isInBlock={true}
          />
        </>
      )}
    </>
  ) : null;
};

export default PageCardBlock;
